import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpsertProfile, useUpdateMe } from '../hooks/useAuth';
import { useT } from '../hooks/useT';
import { useSettingsStore } from '../store/settingsStore';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import logo from '../assets/logo140.png';

// Detect if arriving from a Supabase email confirmation link
function isEmailConfirmation() {
  const hash = window.location.hash;
  return hash.includes('type=signup') || hash.includes('type=email_change');
}

const CURRENCIES = [
  { value: 'BRL', label: 'BRL — Brazilian Real (R$)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'EUR', label: 'EUR — Euro (€)' },
];

const COUNTRIES = [
  { value: 'BR', label: '🇧🇷 Brazil' },
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'EU', label: '🇪🇺 Europe' },
  { value: 'OTHER', label: '🌍 Rest of the World' },
];

const EXPENSE_CATEGORIES = [
  { slug: 'housing', label: '🏠 Housing / Rent' },
  { slug: 'food', label: '🍽️ Food & Groceries' },
  { slug: 'transport', label: '🚗 Transport' },
  { slug: 'utilities', label: '💡 Utilities' },
  { slug: 'health', label: '🏥 Health & Insurance' },
  { slug: 'entertainment', label: '🎬 Entertainment' },
  { slug: 'subscriptions', label: '📱 Subscriptions' },
  { slug: 'education', label: '📚 Education' },
  { slug: 'other', label: '📦 Other' },
];

const RISK_PROFILES = [
  { value: 'conservative', label: '🛡️ Conservative', description: 'Safety first — fixed income instruments' },
  { value: 'moderate', label: '⚖️ Moderate', description: 'Balanced portfolio — mix of fixed and variable income' },
  { value: 'aggressive', label: '🚀 Aggressive', description: 'Growth-focused — stocks, FIIs, higher volatility' },
];

export default function ProfileSetupPage() {
  const navigate = useNavigate();
  const t = useT();
  const { language, toggleLanguage } = useSettingsStore();
  const { mutateAsync: upsertProfile, isPending: isSavingProfile } = useUpsertProfile();
  const { mutateAsync: updateMe, isPending: isSavingUser } = useUpdateMe();
  const isPending = isSavingProfile || isSavingUser;

  const [step, setStep] = useState(1);
  const [showConfirmedBanner, setShowConfirmedBanner] = useState(isEmailConfirmation);
  const [income, setIncome] = useState('');
  const [savings, setSavings] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');
  const [riskProfile, setRiskProfile] = useState('moderate');
  const [expenses, setExpenses] = useState(
    EXPENSE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.slug]: '' }), {})
  );
  const [error, setError] = useState('');

  async function handleSubmit() {
    const expenseList = Object.entries(expenses)
      .filter(([, v]) => v !== '' && parseFloat(v) > 0)
      .map(([slug, amount]) => ({ category_slug: slug, amount: parseFloat(amount), is_fixed: ['housing', 'utilities'].includes(slug) }));

    try {
      await Promise.all([
        upsertProfile({
          monthly_income: parseFloat(income),
          current_savings: parseFloat(savings) || 0,
          risk_profile: riskProfile,
          expenses: expenseList,
        }),
        updateMe({ country, currency }),
      ]);
      navigate('/goals');
    } catch (err) {
      setError(err.response?.data?.error?.message || t('setup.error'));
    }
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-xl">

        {/* Language toggle */}
        <div className="flex justify-end mb-4">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm transition-colors px-2 py-1 rounded-lg hover:bg-surface-card border border-transparent hover:border-surface-border"
            title={language === 'en' ? 'Mudar para Português' : 'Switch to English'}
          >
            🌐 {language === 'en' ? 'PT' : 'EN'}
          </button>
        </div>

        {showConfirmedBanner && (
          <div className="flex items-start gap-3 mb-6 p-4 bg-green-900/25 border border-green-700 rounded-xl">
            <span className="text-xl">✅</span>
            <div className="flex-1">
              <p className="text-green-300 font-medium text-sm">{t('setup.confirmed_title')}</p>
              <p className="text-green-400/80 text-xs mt-0.5">{t('setup.confirmed_sub')}</p>
            </div>
            <button
              onClick={() => setShowConfirmedBanner(false)}
              className="text-green-500 hover:text-green-300 text-lg leading-none"
              aria-label="Dismiss"
            >×</button>
          </div>
        )}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src={logo} alt="Plannizzi AI" className="h-10 w-auto" />
            <div className="flex flex-col leading-tight text-left">
              <span className="font-bold text-base text-slate-100">Plannizzi AI</span>
              <span className="text-xs text-slate-500">{t('brand.slogan')}</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-100">{t('setup.title')}</h1>
          <p className="text-slate-400 mt-1">{t('setup.subtitle')}</p>
          <div className="flex items-center gap-2 justify-center mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 w-10 rounded-full transition-colors ${step >= s ? 'bg-brand-500' : 'bg-surface-hover'}`} />
            ))}
          </div>
        </div>

        <div className="card">
          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <h2 className="text-lg font-semibold text-slate-200">{t('setup.step1.title')}</h2>
              <Input
                label={t('setup.step1.income')}
                type="number"
                placeholder="e.g. 8000"
                min="0"
                step="0.01"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                required
              />
              <Input
                label={t('setup.step1.savings')}
                type="number"
                placeholder="e.g. 15000"
                min="0"
                step="0.01"
                value={savings}
                onChange={(e) => setSavings(e.target.value)}
              />
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t('setup.step1.country')}</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="input w-full"
                  required
                >
                  <option value="" disabled>{t('setup.step1.country_placeholder')}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">{t('setup.step1.currency')}</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="input w-full"
                  required
                >
                  <option value="" disabled>{t('setup.step1.currency_placeholder')}</option>
                  {CURRENCIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <Button
                className="w-full"
                onClick={() => setStep(2)}
                disabled={!income || parseFloat(income) <= 0 || !country || !currency}
              >
                {t('common.next')}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <h2 className="text-lg font-semibold text-slate-200">{t('setup.step2.title')}</h2>
              <p className="text-sm text-slate-500">{t('setup.step2.subtitle')}</p>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {EXPENSE_CATEGORIES.map((c) => (
                  <div key={c.slug} className="flex items-center gap-3">
                    <label className="text-sm text-slate-400 w-44 shrink-0">{t(`expense.cat.${c.slug}`)}</label>
                    <input
                      type="number"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      value={expenses[c.slug]}
                      onChange={(e) => setExpenses((prev) => ({ ...prev, [c.slug]: e.target.value }))}
                      className="input flex-1"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="secondary" onClick={() => setStep(1)} className="flex-1">{t('common.back')}</Button>
                <Button className="flex-1" onClick={() => setStep(3)}>{t('common.next')}</Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 animate-fade-in">
              <h2 className="text-lg font-semibold text-slate-200">{t('setup.step3.title')}</h2>
              <p className="text-sm text-slate-500">{t('setup.step3.subtitle')}</p>
              <div className="space-y-3">
                {RISK_PROFILES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRiskProfile(r.value)}
                    className={`w-full p-4 rounded-xl text-left border transition-colors ${
                      riskProfile === r.value
                        ? 'bg-brand-600/10 border-brand-600 text-brand-300'
                        : 'bg-surface-hover border-surface-border text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <p className="font-medium">{t(`risk.${r.value}.label`)}</p>
                    <p className="text-sm opacity-70 mt-0.5">{t(`risk.${r.value}.desc`)}</p>
                  </button>
                ))}
              </div>

              {error && (
                <div className="p-3 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="secondary" onClick={() => setStep(2)} className="flex-1">{t('common.back')}</Button>
                <Button className="flex-1" onClick={handleSubmit} loading={isPending}>
                  {t('setup.save')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
